import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis
} from "recharts";

import { Event } from "lib/build/events";

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from "src/common/components/Card";
import { ChartTooltip, ChartWrapper } from "src/common/components/Chart";
import {
	ChartTooltipLabel,
	ChartTooltipValue
} from "src/common/components/Chart/ChartTooltip";

const messages = defineMessages({
	title: {
		id: "charts.bmi_trend.title",
		defaultMessage: "BMI Trend"
	},
	bmi: {
		id: "charts.bmi_trend.bmi",
		defaultMessage: "BMI"
	}
});

interface BmiTrendChartProps {
	events: Event<number>[];
}

export const BmiTrendChart: React.FunctionComponent<BmiTrendChartProps> = ({
	events
}) => {
	const { formatDate } = useIntl();
	const data = React.useMemo(
		() =>
			events.map(event => ({
				timestamp: formatDate(event.timestamp, {
					year: "numeric",
					month: "numeric",
					day: "numeric"
				}),
				bmi: event.value
			})),
		[events, formatDate]
	);
	if (data.length < 2) {
		return null;
	}
	return (
		<Card>
			<CardHeader>
				<CardTitle>
					<FormattedMessage {...messages.title} />
				</CardTitle>
			</CardHeader>
			<CardContent>
				<ChartWrapper>
					<ResponsiveContainer height={200}>
						<LineChart data={data}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="timestamp" />
							<YAxis />
							<Tooltip
								wrapperStyle={{ outline: "none" }}
								content={<CustomTooltip />}
							/>
							<Line
								type="monotone"
								dataKey="bmi"
								stroke="var(--color-record)"
							/>
						</LineChart>
					</ResponsiveContainer>
				</ChartWrapper>
			</CardContent>
		</Card>
	);
};

interface CustomTooltipProps {
	active?: boolean;
	label?: string;
	payload?: {
		value: string;
		stroke: string;
	}[];
}

const CustomTooltip: React.FunctionComponent<CustomTooltipProps> = ({
	active,
	payload,
	label
}) => {
	if (active === true && payload !== undefined && payload.length) {
		return (
			<ChartTooltip>
				<ChartTooltipLabel>{label}</ChartTooltipLabel>
				<ChartTooltipValue style={{ color: payload[0].stroke }}>
					<FormattedMessage {...messages.bmi} />: {payload[0].value}
				</ChartTooltipValue>
			</ChartTooltip>
		);
	}
	return null;
};
